<template>
  <component
    v-if="$slots.default"
    :is="tag"
    :to="to"
    v-bind="$attrs"
    :disabled="disabled"
    @click="handleClick"
  >
    <slot />
  </component>
  <component
    v-else
    :is="tag"
    :to="to"
    v-bind="$attrs"
    :disabled="disabled"
    @click="handleClick"
  />
</template>

<script lang="ts">
export default {
  name: 'OPanelLink',
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { useNuxtApp } from '#app'

import {
  ODropdownItem,
  OStatistic,
} from '@/components/elements'

import { buildHash } from '~/stores/usePanelStore'

const { $app, $panel } = useNuxtApp()

interface Props {
  // Path form: "/customer/edit?id=123". Parsed by $panel.create against the registry.
  path?: string
  // Typed panel key form: "customer/edit". Preferred for new code.
  panel?: string
  props?: Record<string, any>
  events?: Record<string, any>
  tag?: string
  disabled?: boolean
  app?: string
}

const props = withDefaults(defineProps<Props>(), {
  path: undefined,
  panel: undefined,
  props: () => ({}),
  events: () => ({}),
  tag: 'nuxt-link',
  disabled: false,
  app: undefined,
})

const emit = defineEmits<{
  (e: 'click', event: Event): void
}>()

// Anchor href — navigates only the URL fragment so middle-click / copy-link /
// right-click land on the same panel hash the click handler will open.
// Using `hash` (not `path`) is the unambiguous Vue Router form: nuxt-link's
// internal router.push preserves the current path and swaps only the fragment.
const to = computed(() => {
  if (props.panel) {
    return { hash: buildHash(props.panel, props.props || {}) }
  }
  if (props.path) {
    const stripped = props.path.startsWith('/') ? props.path.slice(1) : props.path
    return { hash: '#' + stripped }
  }
  return { hash: '' }
})

function handleClick ($event: Event) {
  $event?.preventDefault?.()

  if (props.disabled) return

  if (($app as any)?.isApp && props.app) {
    ;($app as any).navigate(props.app)
    emit('click', $event)
    return
  }

  if (props.panel) {
    ($panel as any).open(props.panel, props.props || {})
  } else if (props.path) {
    ($panel as any).create(props.path, {
      props: props.props,
      events: props.events,
    })
  }

  emit('click', $event)
}
</script>
